<template>
    <div :class="className" :disabled="disabled" @click.stop="toggle">
        <div class="v-switch-core" :style="coreStyle">
            <div class="v-switch-button" :style="buttonStyle" />
            <template v-if="labels">
                <div class="v-switch-label v-left" :style="labelStyle" v-if="toggled">
                    <slot name="checked">
                        {{ labelChecked }}
                    </slot>
                </div>
                <div class="v-switch-label v-right" :style="labelStyle" v-else>
                    <slot name="unchecked">
                        {{ labelUnchecked }}
                    </slot>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    const DEFAULT_COLOR_CHECKED = '#0d47a1';
    const DEFAULT_COLOR_UNCHECKED = '#bfcbd9';
    const DEFAULT_LABEL_CHECKED = 'on';
    const DEFAULT_LABEL_UNCHECKED = 'off';
    const DEFAULT_SWITCH_COLOR = '#fff';

    import isString from 'lodash/isString';
    import has from 'lodash/has';
    import '../../../variables.scss';

    export default {
        name: 'FormSquareSwitch',
        props: {
            value: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            tag: {
                type: String,
            },
            sync: {
                type: Boolean,
                default: false,
            },
            color: {
                type: [String, Object],
                validator(value) {
                    return (
                        isString(value) || has(value, 'checked') || has(value, 'unchecked') || has(value, 'disabled')
                    );
                },
            },
            switchColor: {
                type: [String, Object],
                validator(value) {
                    return isString(value) || has(value, 'checked') || has(value, 'unchecked');
                },
            },
            cssColors: {
                type: Boolean,
                default: false,
            },
            labels: {
                type: [Boolean, Object],
                default: false,
                validator(value) {
                    return typeof value === 'object' ? value.checked || value.unchecked : typeof value === 'boolean';
                },
            },
            paddingLeftButton: {
                type: Number,
                default: 0.2,
            },
            paddingRightButton: {
                type: Number,
                default: 0.2,
            },
            isToolbar: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            className() {
                let { toggled, disabled } = this;

                return [
                    'vue-js-switch',
                    {
                        toggled,
                        disabled,
                    },
                ];
            },
            coreStyle() {
                return {
                    height: this.em(this.isToolbar ? 2.4 : null),
                    backgroundColor: this.cssColors ? null : this.disabled ? this.colorDisabled : this.colorCurrent,
                };
            },
            buttonStyle() {
                const background = this.switchColor ? this.switchColorCurrent : null;
                return {
                    right: this.toggled ? this.em(this.paddingRightButton) : undefined,
                    left: !this.toggled ? this.em(this.paddingLeftButton) : undefined,
                    top: this.em(this.isToolbar ? 0.2 : null),
                    height: this.em(this.isToolbar ? 2 : null),
                    background,
                };
            },
            labelStyle() {
                return {
                    'padding-top': this.em(this.isToolbar ? 0.2 : null),
                };
            },
            colorChecked() {
                let { color } = this;

                if (!this.isObject(color)) {
                    return color || DEFAULT_COLOR_CHECKED;
                }

                return this.get(color, 'checked', DEFAULT_COLOR_CHECKED);
            },
            colorUnchecked() {
                return this.get(this.color, 'unchecked', DEFAULT_COLOR_UNCHECKED);
            },
            colorDisabled() {
                return this.get(this.color, 'disabled', this.colorCurrent);
            },
            colorCurrent() {
                return this.toggled ? this.colorChecked : this.colorUnchecked;
            },
            labelChecked() {
                return this.get(this.labels, 'checked', DEFAULT_LABEL_CHECKED);
            },
            labelUnchecked() {
                return this.get(this.labels, 'unchecked', DEFAULT_LABEL_UNCHECKED);
            },
            switchColorChecked() {
                return this.get(this.switchColor, 'checked', DEFAULT_SWITCH_COLOR);
            },
            switchColorUnchecked() {
                return this.get(this.switchColor, 'unchecked', DEFAULT_SWITCH_COLOR);
            },
            switchColorCurrent() {
                let { switchColor } = this;

                if (!this.isObject(this.switchColor)) {
                    return this.switchColor || DEFAULT_SWITCH_COLOR;
                }

                return this.toggled ? this.switchColorChecked : this.switchColorUnchecked;
            },
        },
        watch: {
            value(value) {
                if (this.sync) {
                    this.toggled = !!value;
                }
            },
        },
        data() {
            return {
                toggled: !!this.value,
            };
        },
        methods: {
            toggle(event) {
                const toggled = !this.toggled;

                if (!this.sync) {
                    this.toggled = toggled;
                }

                this.$emit('input', toggled);
                this.$emit('change', {
                    value: toggled,
                    tag: this.tag,
                    srcEvent: event,
                });
            },
            translate3d(x, y, z = '0px') {
                return `translate3d(${x}, ${y}, ${z})`;
            },
            isString(value) {
                return typeof value === 'string';
            },
            isBoolean(value) {
                return typeof value === 'boolean';
            },
            isObject(value) {
                return typeof value === 'object';
            },
            has(object, key) {
                return this.isObject(object) && object.hasOwnProperty(key);
            },
            get(object, key, defaultValue) {
                return this.has(object, key) ? object[key] : defaultValue;
            },
            em(value) {
                return `${value}em`;
            },
            rem(value) {
                return `${value}rem`;
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    @import '../../../variables.scss';

    .vue-js-switch {
        display: inline-block;
        position: relative;
        user-select: none;
        cursor: pointer;
        vertical-align: middle;
        width: 100%;

        .v-switch-label {
            position: absolute;
            top: 0;
            font-weight: 400;
            color: white;
            z-index: 1;
            font-size: 1rem;
            padding-top: 0.4em;

            &.v-left {
                left: 0.75em;
            }

            &.v-right {
                right: 0.75em;
            }
        }

        .v-switch-core {
            display: block;
            position: relative;
            box-sizing: border-box;
            height: $floating-label-input-height-active;
            outline: 0;
            margin: 0;
            transition: border-color 0.3s, background-color 0.3s;
            user-select: none;
            border-radius: 0.3em;

            .v-switch-button {
                display: block;
                position: absolute;
                overflow: hidden;
                background-color: #fff;
                z-index: 2;
                width: 1.2em;
                height: 2.3em;
                border-radius: 0.1em;
                top: 0.25em;
            }
        }

        &.disabled {
            pointer-events: none;
            opacity: 0.6;
        }
    }
</style>
